import { hasOwn } from '@farmart-engineering/event-contracts/definition';
import catalog from './catalog';
import { domainEvents } from './contracts';
import {
	validatePayload,
	type PayloadSchema,
} from '@farmart-engineering/event-contracts/definition';

const definitions = new Map(
	Object.values(domainEvents).map(definition => [definition.type, definition]),
);
const mappings = new Map(
	catalog.events.flatMap(event =>
		event.legacy.map(
			legacy => [legacy.eventName as string, { event, legacy }] as const,
		),
	),
);
// Enrichment remains on the MoEngage path. It is not silently appended to strict payloads.
const enrichment = new Set([
	'Language',
	'moe_source',
	'moe_source_medium',
	'moe_source_campaign_name',
	'moe_source_campaign_id',
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_id',
	'utm_content',
	'utm_term',
]);
export type DomainMapping =
	| { kind: 'unmapped' }
	| { kind: 'invalid'; type: string; field: string }
	| {
			kind: 'typed';
			definition: (typeof domainEvents)[keyof typeof domainEvents];
			data: Record<string, unknown>;
	  };

export function mapDomainEvent(
	name: string,
	attributes: unknown,
): DomainMapping {
	const match = mappings.get(name);
	if (!match) return { kind: 'unmapped' };
	const { event, legacy } = match;
	const invalid = (field: string): DomainMapping => ({
		kind: 'invalid',
		type: event.type,
		field,
	});
	if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes))
		return invalid('/attributes');
	const fields = legacy.fields as Record<string, string>;
	const excluded = legacy.excluded as readonly string[];
	const omitEmpty = legacy.omitEmpty as readonly string[];
	const data: Record<string, unknown> = { ...legacy.constants };
	if ('variants' in legacy) {
		const input = attributes as Record<string, unknown>;
		const matches = legacy.variants.filter(
			variant =>
				Object.entries(variant.equals).every(
					([key, value]) => hasOwn(input, key) && input[key] === value,
				) &&
				variant.absent.every(key => !hasOwn(input, key)) &&
				variant.present.every(
					key => hasOwn(input, key) && input[key] !== undefined,
				),
		);
		if (matches.length !== 1) return invalid('/attributes/variant');
		Object.assign(data, matches[0].constants);
	}

	for (const [key, value] of Object.entries(attributes)) {
		if (enrichment.has(key) || excluded.includes(key)) continue;
		if (!hasOwn(fields, key)) return invalid('/attributes/additionalProperties');
		if (value === undefined || (value === '' && omitEmpty.includes(key)))
			continue;
		const target = fields[key];
		// A screen label can originate from a route; never retain query/fragment values.
		data[target] =
			target === 'screenName' && typeof value === 'string'
				? value.split(/[?#]/)[0]
				: value;
	}
	const error = validatePayload(event.payloadSchema as PayloadSchema, data);
	if (error) return invalid(error);
	const definition = definitions.get(event.type);
	if (!definition) return invalid('/contract');
	return { kind: 'typed', definition, data };
}
